type AccountLike = {
  tenantId?: string | null;
  homeAccountId?: string | null;
};

const ACCOUNT_SELECTION_STORAGE_PREFIX = 'AccountHomeId';

export function isSpecificTenant(tenantId?: string | null): boolean {
  return !!tenantId && !['organizations', 'common', 'consumers'].includes(tenantId.toLowerCase());
}

export function getAccountSelectionStorageKey(tenantId: string): string {
  return `${ACCOUNT_SELECTION_STORAGE_PREFIX}:${tenantId}`;
}

export function readStoredAccountHomeId(tenantId?: string | null): string | null {
  if (!tenantId) {
    return null;
  }

  return localStorage.getItem(getAccountSelectionStorageKey(tenantId));
}

export function persistStoredAccountSelection(account?: AccountLike | null): void {
  if (!account?.tenantId || !account.homeAccountId) {
    return;
  }

  localStorage.setItem(getAccountSelectionStorageKey(account.tenantId), account.homeAccountId);
}

export function clearStoredAccountSelection(tenantId?: string | null): void {
  if (!tenantId) {
    return;
  }

  localStorage.removeItem(getAccountSelectionStorageKey(tenantId));
}

export function clearAllStoredAccountSelections(): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(`${ACCOUNT_SELECTION_STORAGE_PREFIX}:`)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

export function resolveAccountForTenant<T extends AccountLike>(
  accounts: T[],
  activeAccount?: T | null,
  tenantId?: string | null,
): T | null {
  if (isSpecificTenant(tenantId)) {
    const storedHomeAccountId = readStoredAccountHomeId(tenantId);

    if (storedHomeAccountId) {
      const exactMatch = accounts.find(
        (account) => account.tenantId === tenantId && account.homeAccountId === storedHomeAccountId,
      );
      if (exactMatch) {
        return exactMatch;
      }
    }

    if (activeAccount?.tenantId === tenantId) {
      if (!storedHomeAccountId || activeAccount.homeAccountId === storedHomeAccountId) {
        return activeAccount;
      }
    }

    // A removed saved profile requires an explicit sign-in/selection, not another person.
    if (storedHomeAccountId) return null;

    return accounts.find((account) => account.tenantId === tenantId) ?? null;
  }

  return activeAccount ?? accounts[0] ?? null;
}
